import React, { useEffect, useRef } from 'react';
import { View, Pressable, StyleSheet, Animated, Easing, Platform } from 'react-native';
import { Paprika, PaprikaShadows } from '../../../styles/theme';

interface Props {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

export function PantryDrawer({ visible, onClose, children }: Props) {
  const translateX = useRef(new Animated.Value(420)).current;
  const scrimOpacity = useRef(new Animated.Value(0)).current;

  // Respect prefers-reduced-motion by skipping animation
  const prefersReduced =
    Platform.OS === 'web' &&
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const duration = prefersReduced ? 0 : 280;

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.timing(translateX, {
          toValue: 0,
          duration,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(scrimOpacity, {
          toValue: 1,
          duration,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(translateX, {
          toValue: 420,
          duration: prefersReduced ? 0 : 220,
          easing: Easing.in(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(scrimOpacity, {
          toValue: 0,
          duration: prefersReduced ? 0 : 180,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible, translateX, scrimOpacity, duration, prefersReduced]);

  if (!visible) {
    return null;
  }

  return (
    <View style={styles.container}>
      {/* Scrim */}
      <Animated.View style={[styles.scrim, { opacity: scrimOpacity }]}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
      </Animated.View>

      {/* Drawer panel */}
      <Animated.View
        style={[
          styles.panel,
          { transform: [{ translateX }] },
        ]}
      >
        {children}
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    inset: 0,
    zIndex: 40,
    pointerEvents: 'box-none',
  },
  scrim: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(30, 20, 12, 0.34)',
    zIndex: 40,
  },
  panel: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: 420,
    backgroundColor: Paprika.background,
    zIndex: 50,
    ...PaprikaShadows.modal,
    display: 'flex',
    flexDirection: 'column',
  },
});
